


















































































































































































































































































































































































































import Vue from 'vue';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const ClickOutside = require('vue-click-outside');

// Use Vue.extend so `this.axios` and instance refs type-check the same as
// the rest of the codebase (vue-axios is registered globally on the prototype).
export default Vue.extend({
  'name': 'AdminAnalytics',
  'directives': { ClickOutside },
  'filters': {
    'n' (value: number): string {
      if (value === undefined || value === null) return '—';
      return Number(value).toLocaleString();
    }
  },
  data () {
    return {
      'loading': false,
      'exporting': false,
      'error': null as string | null,
      'snapshot': null as any,
      'loadedAt': null as Date | null,
      'donutColours': ['#c41e3a', '#1f78b4', '#33a02c', '#ff7f00', '#6a3d9a'],
      'tsWidth': 1100,
      'tsHeight': 240,
      'tsBarWidth': 56,
      // Jun 2026 filters
      'allDealers': [] as Array<{ id: number, name: string }>,
      'selectedDealerIds': [] as number[],
      'typeFilter': '' as '' | 'Auto' | 'Building',
      'branchPickerOpen': false,
      // Jun 2026 — period filter. "all" = no date constraint (today's
      // default). Presets 1m/3m/6m/1y compute relative-to-now on each refresh
      // so the dashboard is always honest about what "Past 3 months" means
      // when read across days. Custom uses customFrom/customTo as ISO dates.
      'periodFilter': 'all' as 'all' | '1m' | '3m' | '6m' | '1y' | 'custom',
      'customFrom': '' as string,
      'customTo': '' as string,
      // Jun 2026 — drill-down popover state.
      'drilldownOpen': false,
      'drilldownKind': '' as '' | 'make' | 'model' | 'product',
      'drilldownParams': {} as { 'make'?: string, 'model'?: string, 'productId'?: number },
      'drilldownData': null as null | { 'total': number, 'totalSqFt'?: number, 'branches': Array<{ 'dealerId': number, 'dealerName': string, 'count': number, 'sqFt'?: number, 'pct': number }> },
      'drilldownLoading': false,
      'drilldownError': '' as string
    };
  },
  'computed': {
    // ───── normalised accessors (camelCase JSON from .NET) ─────
    carMakes (): any[] { return this.snapshot?.carMakes || []; },
    topModels (): any[] { return this.snapshot?.topModels || []; },
    topProducts (): any[] { return this.snapshot?.topProducts || []; },
    states (): any[] { return this.snapshot?.states || []; },
    colours (): any[] { return this.snapshot?.colours || []; },
    statusBreakdown (): any[] { return this.snapshot?.statusBreakdown || []; },
    warrantyTypes (): any[] { return this.snapshot?.warrantyTypes || []; },
    dealerLeaderboard (): any[] { return this.snapshot?.dealerLeaderboard || []; },
    registrationsByMonth (): any[] { return this.snapshot?.registrationsByMonth || []; },

    totalWarranty (): number { return this.warrantyTypes.reduce((a: number, w: any) => a + w.count, 0); },
    // Jun 2026 fix: previously summed the *top 15* car-make counts (whatever
    // CarMakeBreakdown returned), so any vehicles with a make outside the top
    // 15 were silently dropped — the tile read "1,308 auto" while the donut
    // said 1,543. Source of truth is the WarrantyTypes aggregate, which counts
    // every Auto warranty.
    totalAutoVehicles (): number {
      const wt = this.warrantyTypes.find((w: any) => w.type === 'Auto');
      return wt ? wt.count : 0;
    },
    totalDealers (): number { return this.dealerLeaderboard.length; },
    // Jun 2026: role check — dealers see a scoped dashboard so the branches
    // picker is hidden and the dealer-leaderboard panel is meaningless.
    isDealer (): boolean { return this.$store.getters.isDealer; },
    // Jun 2026 — drill-down popover labels.
    drilldownEyebrow (): string {
      if (this.drilldownKind === 'make') return 'Branches contributing to';
      if (this.drilldownKind === 'model') return 'Branches installing';
      if (this.drilldownKind === 'product') return 'Branches using';
      return '';
    },
    drilldownTitle (): string {
      const p = this.drilldownParams as any;
      if (this.drilldownKind === 'make') return p.make;
      if (this.drilldownKind === 'model') return `${p.make} · ${p.model}`;
      if (this.drilldownKind === 'product') return p.model || 'Product';
      return '';
    },
    // Jun 2026: human-readable summary of which filters are active.
    filterSummary (): string {
      const parts: string[] = [];
      if (this.isDealer) parts.push('Your branch');
      if (!this.isDealer && this.selectedDealerIds.length) {
        const names = this.allDealers
          .filter((d) => this.selectedDealerIds.indexOf(d.id) !== -1)
          .map((d) => d.name);
        parts.push(names.length <= 3 ? names.join(', ') : (this.selectedDealerIds.length + ' branches'));
      }
      if (this.typeFilter) parts.push(this.typeFilter + ' only');
      const periodLabel: Record<string, string> = {
        '1m': 'Past 1 month',
        '3m': 'Past 3 months',
        '6m': 'Past 6 months',
        '1y': 'Past 1 year',
        'custom': this.customFrom && this.customTo ? `${this.customFrom} – ${this.customTo}` : 'Custom range'
      };
      if (this.periodFilter !== 'all') parts.push(periodLabel[this.periodFilter] || '');
      return parts.filter(Boolean).join(' · ');
    },

    maxCarMakeCount (): number { return Math.max(1, ...this.carMakes.map((r: any) => r.count)); },
    maxStateCount (): number { return Math.max(1, ...this.states.map((r: any) => r.count)); },
    maxColourCount (): number { return Math.max(1, ...this.colours.map((r: any) => r.count)); },
    maxStatusCount (): number { return Math.max(1, ...this.statusBreakdown.map((r: any) => r.count)); },

    tsPeak (): number { return Math.max(1, ...this.registrationsByMonth.map((p: any) => p.count)); },
    tsPoints (): any[] {
      const series = this.registrationsByMonth;
      if (!series.length) return [];
      const max = this.tsPeak;
      const left = 36; const right = 36; const top = 30; const bottom = 32;
      const usableW = this.tsWidth - left - right;
      const usableH = this.tsHeight - top - bottom;
      const step = series.length > 1 ? usableW / (series.length - 1) : 0;
      return series.map((p: any, i: number) => ({
        'x': left + i * step,
        'y': top + (1 - p.count / max) * usableH,
        'count': p.count,
        'label': `${String(p.year).slice(2)}-${String(p.month).padStart(2, '0')}`
      }));
    },

    kpiTiles (): any[] {
      const s = this.snapshot;
      if (!s) return [];
      const topMake = this.carMakes[0];
      const topProduct = this.topProducts[0];
      const topState = this.states[0];
      // Jun 2026 fix: the breakdown text used `totalAutoVehicles` which is the
      // sum of *top 15* makes only — for branches with many makes it under-
      // counted and the tile read "1308 auto" while the donut said 1543.
      // Use the warrantyTypes breakdown (authoritative full count) for both the
      // sub-text and the % market share denominator.
      const autoCount = (this.warrantyTypes.find((w: any) => w.type === 'Auto') || { 'count': 0 }).count;
      const buildingCount = (this.warrantyTypes.find((w: any) => w.type === 'Building') || { 'count': 0 }).count;
      // Jul 2026 — for "Top X" tiles the answer to the question in the label
      // is the *name* (TOYOTA, UVP 0560, Sarawak), not the count. Show the
      // name as the primary value; count + share become the subtitle.
      return [
        {
          'label': 'Total Warranties',
          'value': this.totalWarranty,
          'sub': this.formatNumber(autoCount) + ' auto · ' + this.formatNumber(buildingCount) + ' building',
          'tone': 'tone-iris'
        },
        {
          'label': 'Top Car Make',
          'value': topMake ? topMake.make : '—',
          'valueIsText': true,
          'sub': topMake
            ? this.formatNumber(topMake.count) + ' vehicles · ' + this.pct(topMake.count, autoCount) + '% share'
            : '—',
          'tone': 'tone-blue'
        },
        {
          'label': 'Top Product',
          'value': topProduct ? topProduct.model : '—',
          'valueIsText': true,
          'sub': topProduct
            ? this.formatNumber(topProduct.installCount) + ' installs · ' + this.formatNumber(topProduct.totalSqFt) + ' sqft'
            : '—',
          'tone': 'tone-green'
        },
        {
          'label': 'Top State',
          'value': topState ? topState.state : '—',
          'valueIsText': true,
          'sub': topState ? this.formatNumber(topState.count) + ' vehicles' : '—',
          'tone': 'tone-orange'
        }
      ];
    }
  },
  'methods': {
    formatNumber (n: number | string): string {
      if (n === undefined || n === null) return '—';
      const num = typeof n === 'string' ? parseFloat(n) : n;
      if (isNaN(num)) return String(n);
      return num.toLocaleString(undefined, { 'maximumFractionDigits': 0 });
    },
    formatDateTime (d: Date | null): string {
      if (!d) return '';
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    pct (value: number, max: number): number {
      if (!max) return 0;
      return Math.max(1, Math.round((value / max) * 100));
    },
    donutArcs (rows: any[], radius: number): any[] {
      if (!rows || !rows.length) return [];
      const total = rows.reduce((a: number, r: any) => a + r.count, 0) || 1;
      const C = 2 * Math.PI * radius;
      let acc = 0;
      return rows.map((r: any, i: number) => {
        const len = (r.count / total) * C;
        const arc = {
          'colour': this.donutColours[i % this.donutColours.length],
          'dasharray': `${len} ${C - len}`,
          'offset': -acc
        };
        acc += len;
        return arc;
      });
    },
    refresh () {
      this.loading = true;
      this.error = null;
      const params: Record<string, string> = {};
      if (this.selectedDealerIds.length) params.dealerIds = this.selectedDealerIds.join(',');
      if (this.typeFilter) params.type = this.typeFilter;
      // Jun 2026 — resolve the period preset to ISO date strings so the
      // backend gets a concrete window. "all" sends nothing, matching the
      // pre-Jun behaviour.
      const range = this.resolvePeriodRange();
      if (range.from) params.dateFrom = range.from;
      if (range.to) params.dateTo = range.to;
      this.axios.get('admin/analytics', { 'params': params })
        .then((res: any) => {
          this.snapshot = res.data.result || res.data;
          this.loadedAt = new Date();
        })
        .catch((e: any) => {
          this.error = e?.response?.data?.message || 'Failed to load analytics.';
        })
        .finally(() => { this.loading = false; });
    },
    // Jun 2026 filter wiring
    loadDealers () {
      this.axios.get('dealer/list', { 'params': { 'page': 1, 'pageSize': 200 } })
        .then((res: any) => {
          const items = res.data?.result?.items || [];
          this.allDealers = items
            .map((d: any) => ({ 'id': d.id, 'name': d.name }))
            .sort((a: any, b: any) => a.name.localeCompare(b.name));
        })
        .catch(() => { /* swallow; filter falls back to empty list */ });
    },
    selectAllBranches () { this.selectedDealerIds = this.allDealers.map((d) => d.id); this.refresh(); },
    clearBranches () { this.selectedDealerIds = []; this.refresh(); },
    // Jun 2026 fix — explicit open/close so the directive doesn't fight the click.
    toggleBranchPicker () {
      this.branchPickerOpen = !this.branchPickerOpen;
      // If closing via the summary, also re-fetch so the user sees the new total.
      if (!this.branchPickerOpen) this.refresh();
    },
    closeBranchPickerIfOpen () {
      if (!this.branchPickerOpen) return;
      this.branchPickerOpen = false;
      this.refresh();
    },
    setType (t: '' | 'Auto' | 'Building') { this.typeFilter = t; this.refresh(); },

    // ---- Jun 2026 drill-down ----
    // Opens the popover and fires the correct backend endpoint. Reuses the
    // active filter chain (branches, warranty type, period) so the drill-down
    // matches the dashboard's current slice.
    openDrilldown (kind: 'make' | 'model' | 'product', params: { 'make'?: string, 'model'?: string, 'productId'?: number, 'model2'?: string }) {
      if (this.isDealer) return;
      this.drilldownKind = kind;
      this.drilldownParams = params as any;
      this.drilldownOpen = true;
      this.drilldownData = null;
      this.drilldownError = '';
      this.drilldownLoading = true;
      const q: Record<string, string> = {};
      // Same filter shape as refresh().
      if (this.selectedDealerIds.length) q.dealerIds = this.selectedDealerIds.join(',');
      if (this.typeFilter) q.type = this.typeFilter;
      const range = this.resolvePeriodRange();
      if (range.from) q.dateFrom = range.from;
      if (range.to) q.dateTo = range.to;

      let url = '';
      if (kind === 'make') {
        url = 'admin/analytics/make-by-branch';
        q.make = params.make || '';
      } else if (kind === 'model') {
        url = 'admin/analytics/model-by-branch';
        q.make = params.make || '';
        q.model = params.model || '';
      } else {
        url = 'admin/analytics/product-by-branch';
        q.productId = String(params.productId || 0);
      }

      this.axios.get(url, { 'params': q })
        .then((res: any) => { this.drilldownData = res.data.result || null; })
        .catch((e: any) => { this.drilldownError = e?.response?.data?.message || 'Failed to load drill-down.'; })
        .finally(() => { this.drilldownLoading = false; });
    },
    closeDrilldown () {
      this.drilldownOpen = false;
      this.drilldownData = null;
      this.drilldownError = '';
    },
    exportDrilldownCsv () {
      if (!this.drilldownData || !this.drilldownData.branches) return;
      const rows: string[][] = [];
      const isProduct = this.drilldownKind === 'product';
      const header = isProduct ? ['#', 'Branch', 'Count', 'Sqft', '%'] : ['#', 'Branch', 'Count', '%'];
      rows.push(header);
      this.drilldownData.branches.forEach((b: any, i: number) => {
        const row = isProduct
          ? [String(i + 1), b.dealerName, String(b.count), String(b.sqFt || 0), String(b.pct)]
          : [String(i + 1), b.dealerName, String(b.count), String(b.pct)];
        rows.push(row);
      });
      const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { 'type': 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${this.drilldownKind}-${(this.drilldownTitle || '').replace(/[^A-Za-z0-9]+/g, '-')}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    },
    // Jun 2026 — period preset switch. Custom requires from+to before fetching
    // so the dashboard doesn't blink to "nothing in range" while the user is
    // mid-typing dates.
    setPeriod (p: 'all' | '1m' | '3m' | '6m' | '1y' | 'custom') {
      this.periodFilter = p;
      if (p !== 'custom') {
        this.customFrom = '';
        this.customTo = '';
        this.refresh();
      }
    },
    resolvePeriodRange (): { 'from': string, 'to': string } {
      // Backend uses InstallationDate. We send `from` as the start of the
      // window and `to` as today; the chart counts everything installed within.
      const today = new Date();
      const iso = (d: Date) => d.toISOString().slice(0, 10);
      const sub = (months: number): string => {
        const d = new Date(today);
        d.setMonth(d.getMonth() - months);
        return iso(d);
      };
      switch (this.periodFilter) {
        case '1m': return { 'from': sub(1), 'to': iso(today) };
        case '3m': return { 'from': sub(3), 'to': iso(today) };
        case '6m': return { 'from': sub(6), 'to': iso(today) };
        case '1y': return { 'from': sub(12), 'to': iso(today) };
        case 'custom': return { 'from': this.customFrom || '', 'to': this.customTo || '' };
        default: return { 'from': '', 'to': '' };
      }
    },
    exportDatabase () {
      this.exporting = true;
      this.axios.get('admin/export/database', { 'responseType': 'blob' })
        .then((res: any) => {
          const url = window.URL.createObjectURL(new Blob([res.data], { 'type': 'application/zip' }));
          const link = document.createElement('a');
          link.href = url;
          const ts = new Date().toISOString().replace(/[:.]/g, '-');
          link.setAttribute('download', `iris-pro-export-${ts}.zip`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => { alert('Failed to export database.'); })
        .finally(() => { this.exporting = false; });
    }
  },
  mounted () {
    this.refresh();
    // Jun 2026: dealers can't see /dealer/list (it's admin-only) and the
    // branches picker is hidden for them anyway. Skip the call.
    if (!this.isDealer) this.loadDealers();
  }
});
